import type { Row } from '@tanstack/react-table';
import type { InsightsItem } from '@/components/loop-dashboard/types/insightsItem';

export interface RatioData {
  actualRatio: number;
  planRatio: number;
}

export const calculateRatioData = (actualSeconds: number, planSeconds: number): RatioData => {
  const maxValue = Math.max(actualSeconds, planSeconds);
  if (maxValue === 0) {
    return { actualRatio: 0, planRatio: 0 };
  }
  return {
    actualRatio: actualSeconds / maxValue,
    planRatio: planSeconds / maxValue,
  };
};

export const calculateNormalizedRatioData = (
  actualSeconds: number,
  planSeconds: number,
  globalMaxSeconds: number,
): RatioData => {
  if (globalMaxSeconds === 0) {
    return { actualRatio: 0, planRatio: 0 };
  }
  return {
    actualRatio: actualSeconds / globalMaxSeconds,
    planRatio: planSeconds / globalMaxSeconds,
  };
};

type ItemWithSeconds = { actualSeconds: number; planSeconds: number; children?: ItemWithSeconds[] };

export const calculateGlobalMaxSeconds = (items: ItemWithSeconds[]): number => {
  if (items.length === 0) return 0;

  let max = 0;

  const collectMax = (item: ItemWithSeconds): void => {
    max = Math.max(max, item.actualSeconds, item.planSeconds);
    item.children?.forEach(collectMax);
  };

  items.forEach(collectMax);
  return max;
};

export const getRowRatioData = (
  info: { row: Row<InsightsItem> },
  globalMaxSeconds: number,
): RatioData => {
  const { planSeconds, actualSeconds, id } = info.row.original;

  // Total synthetic row (id: 0) normalizes to itself to show actual vs plan distribution
  if (id === 0) {
    return calculateRatioData(actualSeconds, planSeconds);
  }

  // All other rows normalize to the global max for cross-item comparison
  return calculateNormalizedRatioData(actualSeconds, planSeconds, globalMaxSeconds);
};

export const RATIO_DISPLAY_CAP = 1.01; // Prevents visual overflow in ratio bars
